import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { compareByMonthYear } from "@/utils/dateUtils";
import {
  Target, Search, RefreshCw, Phone, Mail, MessageSquare, Video,
  CheckCircle2, RotateCcw, X, ChevronUp, ChevronDown, ExternalLink,
  ArrowUpDown, Download, Printer
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const AllFollowUpsPage = () => {
  const { token } = useAuth();
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search and filter state
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterStatus, setFilterStatus] = useState("pending");
  
  // Sort state
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");

  // Fetch all follow-ups
  const fetchFollowUps = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-ups?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFollowUps(data.follow_ups || []);
      }
    } catch (error) {
      console.error("Error fetching follow-ups:", error);
      toast.error("Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowUps();
  }, []);

  // Filtered and sorted follow-ups
  const filteredSortedFollowUps = useMemo(() => {
    let result = [...followUps];
    
    // Apply status filter
    if (filterStatus !== "all") {
      result = result.filter(f => f.status === filterStatus);
    }
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(f => 
        f.client_code?.toLowerCase().includes(searchLower) ||
        f.customer_name?.toLowerCase().includes(searchLower) ||
        f.notes?.toLowerCase().includes(searchLower) ||
        f.assigned_to?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply type filter
    if (filterType !== "all") {
      result = result.filter(f => f.follow_up_type === filterType);
    }
    
    // Apply priority filter
    if (filterPriority !== "all") {
      result = result.filter(f => f.priority === filterPriority);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "date":
          // Use monthly sorting for dates (Feb 26 > Jan 26 > Dec 25)
          comparison = compareByMonthYear(a.follow_up_date, b.follow_up_date, 'asc');
          break;
        case "client":
          comparison = (a.client_code || "").localeCompare(b.client_code || "");
          break;
        case "customer":
          comparison = (a.customer_name || "").localeCompare(b.customer_name || "");
          break;
        case "priority":
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          comparison = (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3);
          break;
        case "type":
          comparison = (a.follow_up_type || "").localeCompare(b.follow_up_type || "");
          break;
        case "assigned":
          comparison = (a.assigned_to || "").localeCompare(b.assigned_to || "");
          break;
        default:
          break;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
    
    return result;
  }, [followUps, search, filterType, filterPriority, filterStatus, sortBy, sortOrder]);

  // Handle sort click
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300" />;
    }
    return sortOrder === "asc" 
      ? <ChevronUp className="w-4 h-4 ml-1 text-blue-600" />
      : <ChevronDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  // Export to Excel
  const exportToExcel = () => {
    const wsData = [
      ["All Pending Follow-ups Report"],
      [`Generated: ${new Date().toLocaleString()}`],
      [`Total Records: ${filteredSortedFollowUps.length}`],
      [],
      ["#", "Client Code", "Customer Name", "Type", "Date", "Priority", "Assigned To", "Notes", "Status"]
    ];
    
    filteredSortedFollowUps.forEach((f, idx) => {
      wsData.push([
        idx + 1,
        f.client_code || "",
        f.customer_name || "",
        f.follow_up_type || "",
        f.follow_up_date || "",
        f.priority || "",
        f.assigned_to || "",
        f.notes || "",
        f.status || ""
      ]);
    });
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [
      { wch: 5 }, { wch: 12 }, { wch: 25 }, { wch: 10 },
      { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 40 }, { wch: 10 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Follow-ups");
    
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `all_follow_ups_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exported to Excel");
  };

  // Mark as done
  const handleMarkDone = async (followUp) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-ups/${followUp.id}/complete`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ outcome: "completed", completion_notes: "Marked as done" })
      });
      if (response.ok) {
        toast.success("Follow-up marked as done");
        fetchFollowUps();
      }
    } catch (error) {
      toast.error("Failed to update follow-up");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Target className="w-6 h-6 text-blue-600" />
              All Pending Follow-ups
            </h1>
            <p className="text-slate-500 mt-1">Manage and track all your follow-up tasks</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <Download className="w-4 h-4 mr-1" /> Export Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={fetchFollowUps}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[250px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search client, customer, notes..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="px-3 py-1">
                {filteredSortedFollowUps.length} records
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-100">
                    <th className="text-left p-3 font-medium w-12">#</th>
                    <th 
                      className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleSort("client")}
                    >
                      <div className="flex items-center">
                        Client Code <SortIcon column="client" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleSort("customer")}
                    >
                      <div className="flex items-center">
                        Customer Name <SortIcon column="customer" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleSort("type")}
                    >
                      <div className="flex items-center">
                        Type <SortIcon column="type" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleSort("date")}
                    >
                      <div className="flex items-center">
                        Date <SortIcon column="date" />
                      </div>
                    </th>
                    <th 
                      className="text-center p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleSort("priority")}
                    >
                      <div className="flex items-center justify-center">
                        Priority <SortIcon column="priority" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleSort("assigned")}
                    >
                      <div className="flex items-center">
                        Assigned <SortIcon column="assigned" />
                      </div>
                    </th>
                    <th className="text-left p-3 font-medium">Notes</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-center p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="text-center p-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
                      </td>
                    </tr>
                  ) : filteredSortedFollowUps.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center p-8 text-slate-500">
                        No follow-ups found
                      </td>
                    </tr>
                  ) : filteredSortedFollowUps.map((f, idx) => {
                    const isOverdue = f.status === "pending" && new Date(f.follow_up_date) < new Date();
                    return (
                      <tr key={f.id || idx} className="border-b hover:bg-slate-50">
                        <td className="p-3 text-slate-500">{idx + 1}</td>
                        <td className="p-3">
                          <span className="font-medium">{f.client_code}</span>
                        </td>
                        <td className="p-3">{f.customer_name || "-"}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={`text-xs ${
                            f.follow_up_type === 'call' ? 'bg-green-50 text-green-700' :
                            f.follow_up_type === 'email' ? 'bg-blue-50 text-blue-700' :
                            f.follow_up_type === 'whatsapp' ? 'bg-emerald-50 text-emerald-700' :
                            f.follow_up_type === 'meeting' ? 'bg-orange-50 text-orange-700' :
                            'bg-slate-50 text-slate-700'
                          }`}>
                            {f.follow_up_type === 'call' && <Phone className="w-3 h-3 mr-1" />}
                            {f.follow_up_type === 'email' && <Mail className="w-3 h-3 mr-1" />}
                            {f.follow_up_type === 'whatsapp' && <MessageSquare className="w-3 h-3 mr-1" />}
                            {f.follow_up_type === 'meeting' && <Video className="w-3 h-3 mr-1" />}
                            {f.follow_up_type}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div>
                            <span className={isOverdue ? "text-red-600 font-medium" : ""}>
                              {f.follow_up_date}
                            </span>
                            {isOverdue && (
                              <Badge className="ml-2 text-xs bg-red-100 text-red-700">Overdue</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={`text-xs ${
                            f.priority === 'urgent' ? 'bg-red-500 text-white' :
                            f.priority === 'high' ? 'bg-orange-500 text-white' :
                            f.priority === 'medium' ? 'bg-yellow-500 text-white' :
                            'bg-slate-400 text-white'
                          }`}>
                            {f.priority}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm">{f.assigned_to || "-"}</td>
                        <td className="p-3 text-sm max-w-[200px] truncate" title={f.notes}>
                          {f.notes || "-"}
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant="outline" className={`text-xs ${
                            f.status === 'completed' ? 'bg-green-50 text-green-700' :
                            f.status === 'cancelled' ? 'bg-red-50 text-red-700' :
                            'bg-yellow-50 text-yellow-700'
                          }`}>
                            {f.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-center">
                          {f.status === "pending" && (
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleMarkDone(f)}
                                title="Mark as Done"
                              >
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => window.open(`/client-timeline/${f.client_code}`, '_blank')}
                                title="View Timeline"
                              >
                                <ExternalLink className="w-4 h-4 text-blue-600" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AllFollowUpsPage;
